// Use this for global constants
// If specific, then group them under a object.
const TASKLIST_ROLE_DEFINITIONS = Object.freeze({
  MANAGER: { value: 'manager', label: 'Manager' },
  PREPARER: { value: 'preparer', label: 'Preparer' },
  APPROVER: { value: 'approver', label: 'Approver' },
});

const TASKLIST_ROLES = Object.freeze(
  Object.fromEntries(
    Object.entries(TASKLIST_ROLE_DEFINITIONS).map(([key, role]) => [key, role.value]),
  ),
);

const TASKLIST_ROLE_OPTIONS = Object.freeze(Object.values(TASKLIST_ROLE_DEFINITIONS));

const TASKLIST_ROLE_VALUES = Object.freeze(
  TASKLIST_ROLE_OPTIONS.map((role) => role.value),
);

const TASKLIST_NON_MANAGER_ROLE_VALUES = Object.freeze(
  TASKLIST_ROLE_VALUES.filter((value) => value !== TASKLIST_ROLES.MANAGER),
);

export default {
  APP: {
    TITLE: 'Consarc.ai',
  },
  SNACKBAR: {
    DEFAULT_DURATION_MS: 5000,
    ACTION_DURATION_MS: 10000,
  },
  AUTH: {
    SESSION_EXPIRED_CODES: ["UL403", "SESSION_EXPIRED", "auth.session_expired"],
    CLIENT_MISMATCH_CODES: [
      "auth.api_client_mismatch",
      "auth.tenant_mismatch",
    ],
    REDIRECT_DEBOUNCE_MS: 2000,
    HOST_MODES: {
      APP_HOST: 'APP_HOST',
      DEDICATED: 'DEDICATED',
      UNKNOWN: 'UNKNOWN',
    },
    AUTH_MODES: {
      JWT: 'jwt',
      SESSION: 'session',
    },
    LOGIN_STEPS: {
      DISCOVER: 'discover',
      CHOOSE: 'choose',
      CREDENTIALS: 'credentials',
    },
    AUTH_POLICIES: {
      LOCAL_ONLY: 'LOCAL_ONLY',
      HYBRID: 'HYBRID',
      SSO_ONLY: 'SSO_ONLY',
      SSO_WITH_BREAK_GLASS: 'SSO_WITH_BREAK_GLASS',
    },
    /** Paths (relative to Backend base) excluded from silent refresh retry */
    REFRESH_EXCLUDE_PATH_FRAGMENTS: [
      '/v2/auth/local',
      '/v2/auth/refresh',
      '/v2/auth/sso/complete',
      '/v2/auth/login-screens',
      '/v2/auth/okta',
      '/forgotpassword',
      '/setpassword',
      '/setnewpassword',
      '/auth/discover',
      '/auth/login-options',
      '/auth/sso/start',
      '/auth/sso/exchange',
      '/auth/password/eligibility',
    ],
    GENERIC_DISCOVER_ERROR: "We couldn't find an account with that email.",
    INVALID_EMAIL_OR_PASSWORD: 'Invalid email or password',
    SITE_UNAVAILABLE: 'This site is unavailable',
    CLIENT_MISMATCH_MESSAGE:
      'Your workspace selection no longer matches this session. Please sign in again.',
    SIGN_IN_HEADING: 'Sign in',
    DISCOVER_SUBTITLE: 'Access your Consarc secure workspace',
    WORK_EMAIL_LABEL: 'Work Email',
    WORK_EMAIL_PLACEHOLDER: 'name@company.com',
    INVALID_WORK_EMAIL: 'Enter a valid work email address.',
    CONTINUE: 'Continue',
    BACK: 'Back',
    RESET_HEADING: 'Set a new password',
    RESET_SUBTITLE: "Enter your work email and we'll send you a link.",
    RESET_EXISTING: 'Send link',
    RESET_NEW: 'Reset password as a new user',
    RESET_CANCEL: 'Cancel',
    CHECK_INBOX_HEADING: 'Check your inbox',
    CHECK_INBOX_BODY:
      "We've sent a link to {email}. Use it to set your password. It expires in 15 minutes.",
    CHECK_INBOX_NOTICE:
      'Not receiving it? Your administrator can confirm which address is on your account.',
    USE_DIFFERENT_EMAIL: 'Use a different email',
    RESEND_EMAIL: 'Resend email',
    BACK_TO_SIGN_IN: 'Back to sign in',
    RESET_GENERIC_ERROR: 'Error!',
    SET_PASSWORD_HEADING: 'Set your password',
    SET_PASSWORD_SUBTITLE: 'Enter your new password below.',
    SET_PASSWORD_CHOOSE_SUBTITLE: 'Choose a password for {email}.',
    SET_PASSWORD_CHOOSE_SUBTITLE_FALLBACK: 'Choose a password.',
    SET_PASSWORD_EMAIL_LABEL: 'Email',
    SET_PASSWORD_OLD_LABEL: 'Old password',
    SET_PASSWORD_OTP_LABEL: 'OTP',
    SET_PASSWORD_NEW_LABEL: 'New password',
    SET_PASSWORD_CONFIRM_LABEL: 'Confirm password',
    SET_PASSWORD_HINT: 'At least 12 characters, including a number and a symbol.',
    SET_PASSWORD_POLICY:
      'New password must be at least 12 characters and include 1 uppercase, 1 lowercase, 1 number, and 1 special character.',
    SET_PASSWORD_SUBMIT: 'Reset password',
    SET_PASSWORD_CREATE_SUBMIT: 'Set password',
    SET_PASSWORD_CANCEL: 'Cancel',
    SET_PASSWORD_SUCCESS_HEADING: 'Password changed successfully',
    SET_PASSWORD_ALL_SET_HEADING: "You're all set",
    SET_PASSWORD_SUCCESS_BODY:
      'Your password has been set. You can now sign in to Consarc.',
    SET_PASSWORD_CONTINUE: 'Continue to sign in',
    SET_PASSWORD_REDIRECTING: 'Redirecting to login...',
    SET_PASSWORD_GO_HOME: 'Go to home',
    SET_PASSWORD_MISMATCH: "Passwords don't match.",
    SET_PASSWORD_REDIRECT_MS: 3000,
    LOGO_ALT: 'consarc.ai',
    TERMS_LABEL: 'Terms',
    PRIVACY_LABEL: 'Privacy Policy',
    LEGAL_SEPARATOR: '  ·  ',
    LEGAL_NAV_LABEL: 'Legal',
    LOADER_TEXT: 'Loading…',
    CHOOSE_WORKSPACE_TITLE: 'Choose a workspace',
    CHOOSE_WORKSPACE_ACCESS: 'You have access to {count} workspaces.',
    CHOOSE_WORKSPACE_CHANGE: 'Change',
    CHOOSE_WORKSPACE_LAST_USED: 'Last used',
    CHOOSE_WORKSPACE_FALLBACK_NAME: 'Workspace',
    SSO_CALLBACK_SIGNING_IN: 'Signing you in…',
    SSO_CALLBACK_GENERIC_ERROR: 'Sign-in could not be completed.',
    SSO_CALLBACK_BACK: 'Back to login',
  },
  SSO: {
    HOSTING_MODES: {
      SHARED_APP_HOST: 'SHARED_APP_HOST',
      DEDICATED_SUBDOMAIN: 'DEDICATED_SUBDOMAIN',
    },
    AUTH_POLICIES: [
      { value: 'LOCAL_ONLY', text: 'Local only' },
      { value: 'HYBRID', text: 'Hybrid (local + SSO)' },
      { value: 'SSO_ONLY', text: 'SSO only' },
      { value: 'SSO_WITH_BREAK_GLASS', text: 'SSO with break-glass' },
    ],
    CONNECTION_LIFECYCLES: {
      DRAFT: 'DRAFT',
      TESTING: 'TESTING',
      ACTIVE: 'ACTIVE',
      DISABLED: 'DISABLED',
      RETIRED: 'RETIRED',
    },
    DEFAULT_EMAIL_CLAIM:
      'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    /** SPA Cognito return URL — must not use /auth (proxied to Auth BFF) */
    CALLBACK_PATH: '/sso/callback/cognito',
    LOGOUT_COMPLETE_PATH: '/logout/complete',
  },
  AUTH_BFF: {
    DISCOVER: '/auth/discover',
    LOGIN_OPTIONS: '/auth/login-options',
    SSO_START: '/auth/sso/start',
    SSO_EXCHANGE: '/auth/sso/exchange',
    PASSWORD_ELIGIBILITY: '/auth/password/eligibility',
  },
  BULK_FILE_TEMPLATES: {
    EN: "en",
    LABELS_PAGE: "reporting_units_bulk_file",
    ERROR_MESSAGES: "reporting_units_error_messages",
    BULK_SUBMIT_COLUMNS_ACTUAL: [
      "GL Code",
      "Description",
      "Actual Amount",
      "Proforma Amount",
      "Variance Amt(Act vs Pro)",
      "Variance %(Act vs Pro)",
      "Cross Local Ctrl",
      "Cross Local Mngt",
      "Cross Regional Ctrl",
      "Cross Regional Mngt",
      "Local Act vs Pro Commentary",
      "Regional Act vs Pro Commentary",
      "Forecast Amount",
      "Variance Amt(Act vs For)",
      "Variance %(Act vs For)",
      "Cross Local Ctrl",
      "Cross Local Mngt",
      "Cross Regional Ctrl",
      "Cross Regional Mngt",
      "Local Act vs For Commentary",
      "Regional Act vs For Commentary",
      "Status Local",
      "Status Regional",
      "Local Approve/Reject (Yes/No)",
      "Local Approve/Reject Reason",
      "Regional Approve/Reject (Yes/No)",
      "Reject - Local Level (LA/LP)",
      "Regional Approve/Reject Reason",
      "Local Approve & Submit (Yes/No)",
      "Local Approve & Submit Reason",
    ],
    BULK_SUBMIT_COLUMNS_FORECAST: [
      "GL Code",
      "Description",
      "Forecast Amount",
      "Proforma Amount",
      "Variance Amt(For vs Pro)",
      "Variance %(For vs Pro)",
      "Cross Local Ctrl",
      "Cross Local Mngt",
      "Cross Regional Ctrl",
      "Cross Regional Mngt",
      "Local For vs Pro Commentary",
      "Regional For vs Pro Commentary",
      "Previous Forecast Amount",
      "Variance Amt(For vs Prev For)",
      "Variance %(For vs Prev For)",
      "Cross Local Ctrl",
      "Cross Local Mngt",
      "Cross Regional Ctrl",
      "Cross Regional Mngt",
      "Local For vs Prev For Commentary",
      "Regional For vs Prev For Commentary",
      "Status Local",
      "Status Regional",
      "Local Approve/Reject (Yes/No)",
      "Local Approve/Reject Reason",
      "Regional Approve/Reject (Yes/No)",
      "Reject - Local Level (LA/LP)",
      "Regional Approve/Reject Reason",
      "Local Approve & Submit (Yes/No)",
      "Local Approve & Submit Reason",
    ],
    BULK_SUBMIT_COLUMNS_ACTUAL_BS: [
      "GL Code",
      "Description",
      "Actual Amount",
      "Proforma Amount",
      "Variance Amt(Act vs Pro)",
      "Variance %(Act vs Pro)",
      "Cross Local Ctrl",
      "Cross Local Mngt",
      "Cross Regional Ctrl",
      "Cross Regional Mngt",
      "Local Act vs Pro Commentary",
      "Regional Act vs Pro Commentary",
      "Forecast Amount",
      "Variance Amt(Act vs For)",
      "Variance %(Act vs For)",
      "Cross Local Ctrl",
      "Cross Local Mngt",
      "Cross Regional Ctrl",
      "Cross Regional Mngt",
      "Local Act vs For Commentary",
      "Regional Act vs For Commentary",
      "Status Local",
      "Status Regional",
      "Local Approve/Reject (Yes/No)",
      "Local Approve/Reject Reason",
      "Regional Approve/Reject (Yes/No)",
      "Reject - Local Level (LA/LP)",
      "Regional Approve/Reject Reason",
      "Local Approve & Submit (Yes/No)",
      "Local Approve & Submit Reason",
    ],
    BULK_SUBMIT_COLUMNS_FORECAST_BS: [
      "GL Code",
      "Description",
      "Forecast Amount",
      "Proforma Amount",
      "Variance Amt(For vs Pro)",
      "Variance %(For vs Pro)",
      "Cross Local Ctrl",
      "Cross Local Mngt",
      "Cross Regional Ctrl",
      "Cross Regional Mngt",
      "Local For vs Pro Commentary",
      "Regional For vs Pro Commentary",
      "Previous Forecast Amount",
      "Variance Amt(For vs Prev For)",
      "Variance %(For vs Prev For)",
      "Cross Local Ctrl",
      "Cross Local Mngt",
      "Cross Regional Ctrl",
      "Cross Regional Mngt",
      "Local For vs Prev For Commentary",
      "Regional For vs Prev For Commentary",
      "Status Local",
      "Status Regional",
      "Local Approve/Reject (Yes/No)",
      "Local Approve/Reject Reason",
      "Regional Approve/Reject (Yes/No)",
      "Reject - Local Level (LA/LP)",
      "Regional Approve/Reject Reason",
      "Local Approve & Submit (Yes/No)",
      "Local Approve & Submit Reason",
    ],
    BULK_MATERIALITY_RU_LEVEL_UPLOAD: [
      "RU Code",
      "Name of Reporting Unit",
      "Month",
      "Year",
      "LC Amt",
      "LC %",
      "LM Amt",
      "LM %",
      "RC Amt",
      "RC %",
      "RM Amt",
      "RM %",
      "LC Amt",
      "LC %",
      "LM Amt",
      "LM %",
      "RC Amt",
      "RC %",
      "RM Amt",
      "RM %",
    ],
    BULK_MATERIALITY_GL_LEVEL_UPLOAD: [
      "RU Code",
      "Name of Reporting Unit",
      "GL Code",
      "GL Description",
     "Month",
      "Year",
      "LC Amt",
      "LC %",
      "LM Amt",
      "LM %",
      "RC Amt",
      "RC %",
      "RM Amt",
      "RM %",
      "LC Amt",
      "LC %",
      "LM Amt",
      "LM %",
      "RC Amt",
      "RC %",
      "RM Amt",
      "RM %",
    ],
    BULK_MATERIALITY_GL_LEVEL_UPLOAD_INSTRUCTIONS: [
      [null, null],
      [null, "Template to bulk upload materiality details at GL level"],
      [null, null],
      [null, "- RU Codes, GL codes and Period are mandatory columns."],
      [
        null,
        "- The name of the reporting unit and the GL description is not a mandatory column.",
      ],
      [
        null,
        "- Suppose the user updates Local Materiality, all the 3 fields related to Local Materiality computation i.e. Local Control Amount, Local Management Amount and Local Management Percentage should be updated (Please note that the Local and Regional Controls are set to 999,999% by default and cannot be edited). The system does not accept the file even if one of the field is blank. The same logic applies for the Regional Materiality as well.  All the fields for materiality setting within the layer ( Local or Regional ) need to be submitted.",
      ],
      [
        null,
        '- Please note that values added in the "Control %" and "Management %" columns should be in whole numbers and not in decimals. e.g., 10% instead of 0.1%.',
      ],
      [null, "- Currency denomination is in United States Dollars (USD)."],
      [
        null,
        "- All values are in thousands. Example : If the threshold needs to be set at $100,000 then update the value as $100.",
      ],
      [
        null,
        '- Control thresholds percentage values will not be considered since it is already set at 999,999%. Local control and Regional control percentages are non-editable. The default percentage is "999,999%". If any value is entered in the excel file during bulk materiality file upload change request, that value won\'t be considered.',
      ],
      [null, null],
      [
        null,
        "- Ensure the file contains no more than 10,000 line items and is free of errors before uploading.",
      ],
    ],
    BULK_MATERIALITY_RU_LEVEL_UPLOAD_INSTRUCTIONS: [
      [null, null],
      [null, "Template to bulk upload materiality details at RU level"],
      [null, null],
      [null, "- RU Code & Period are mandatory columns."],
      [null, "- The name of the reporting unit is not a mandatory column."],
      [
        null,
        "- Suppose the user updates Local Materiality, all the 3 fields related to Local Materiality computation i.e. Local Control Amount, Local Management Amount and Local Management Percentage should be updated (Please note that the Local and Regional Controls are set to 999,999% by default and cannot be edited). The system does not accept the file even if one of the field is blank. The same logic applies for the Regional Materiality as well.  All the fields for materiality setting within the layer ( Local or Regional ) need to be submitted.",
      ],
      [
        null,
        '- Please note that values added in the "Control %" and "Management %" columns should be in whole numbers and not in decimals. e.g., 10% instead of 0.1%.',
      ],
      [null, "- Currency denomination is in United States Dollars (USD)."],
      [
        null,
        "- All values are in thousands. Example : If the threshold needs to be set at $100,000 then update the value as $100.",
      ],
      [
        null,
        '- Control thresholds percentage values will not be considered since it is already set at 999,999%. Local control and Regional control percentages are non-editable. The default percentage is "999,999%". If any value is entered in the excel file during bulk materiality file upload change request, that value won\'t be considered.',
      ],
      [null, null],
      [
        null,
        "- Ensure the file contains no more than 10,000 line items and is free of errors before uploading.",
      ],
    ],
    BULK_ACCESS_RIGHTS_UPLOAD: [
      "RU Code",
      "Name of Reporting Unit",
      "Local Preparer(s)",
      "Local Approver(s)",
      "Regional Approver(s)",
      "Manager(s)",
    ],
    BULK_ACCESS_RIGHTS_UPLOAD_INSTRUCTIONS: [
      [null, null],
      [null, "Template to bulk upload access details at RU level"],
      [null, null],
      [null, "- RU Codes are mandatory"],
      [
        null,
        "- If the user wants to update only the preparer/approver/manager, the other field can be left blank.",
      ],
      [null, "- The name of the reporting unit is not a mandatory field."],
      [
        null,
        "- Preparer/Approver/Manager for a RU accepts only email id. Multiple emails can be given using comma for separation",
      ],
      [
        null,
        "- Preparer/Approver/Manager for a RU cannot be the same person viz., same email ID.",
      ],
      [
        null,
        "- On upload the application will validate whether the user assigned has access to such reporting units in BFC and only if access is available for such users to BFC will the application make the relevant change in assigning the user roles based on this template",
      ],
      [
        null,
        "- Once this template is uploaded the application will make the necessary changes and forward the same to the admin approver to review and decide on the same",
      ],
      [
        null,
        "- When SOD is disabled, the same user can be assigned to multiple roles within the selected RU",
      ],
      [
        null,
        "- When SOD is disabled and a user is already assigned to an RU, to add additional roles for the same user, include the user entry for both the previously assigned role and all new roles to be assigned",
      ],
    ],
    BULK_WORKFLOW_UPLOAD: [
      "RU Code",
      "Proforma Local Control",
      "Proforma Local Management",
      "Proforma Regional Control",
      "Proforma Regional Management",
      "Forecast Local Control",
      "Forecast Local Management",
      "Forecast Regional Control",
      "Forecast Regional Management",
    ],
    BULK_WORKFLOW_UPLOAD_SHOW_TABLE: [
      "RU Code",
      "RU Name",
      "Proforma Local Control",
      "Proforma Local Management",
      "Proforma Regional Control",
      "Proforma Regional Management",
      "Forecast Local Control",
      "Forecast Local Management",
      "Forecast Regional Control",
      "Forecast Regional Management",
    ],
  },
  UNGROUPED_GL_OFFSET: 10000,
  TASK_LIST_CONSTANTS: {
    TASK_TYPES: {
      NORMAL: "Checklist",
      ACCOUNT_RECONCILIATION: "Account Reconciliation",
    },
    STATUS_DETAILS: {
      PENDING_SUBMISSION_DUE: 'Pending Submission Due',
      PENDING_SUBMISSION_NOT_DUE: 'Pending Submission Not Due',
      PENDING_SUBMISSION_OVERDUE: 'Pending Submission Overdue',
      PENDING_APPROVAL_DUE: 'Pending Approval Due',
      PENDING_APPROVAL_NOT_DUE: 'Pending Approval Not Due',
      PENDING_APPROVAL_OVERDUE: 'Pending Approval Overdue',
      CLOSED_TASK: 'Closed Task'
    },
    STATUS_ICONS: {
      PENDING_SUBMISSION: 'mdi-timer-sand-paused',
      PENDING_SUBMISSION_NOT_DUE: 'mdi-timer-sand-empty',
      PENDING_SUBMISSION_OVERDUE: 'mdi-timer-sand-full',
      PENDING_APPROVAL: 'mdi-timer-sand-paused',
      PENDING_APPROVAL_NOT_DUE: 'mdi-timer-sand-empty',
      PENDING_APPROVAL_OVERDUE: 'mdi-timer-sand-full',
      CLOSED: 'mdi-check-circle'
    },
    STATUS_COLORS: {
      SUBMISSION: '#C69908',
      APPROVAL: '#4285F4',
      CLOSED: 'green'
    },
    ASSERTIONS: [
      "Existence",
      "Occurrence",
      "Rights and obligations",
      "Completeness",
      "Accuracy valuation and allocation",
      "Cut-off",
      "Classification",
      "Presentation",
    ],
    // Canonical role identifiers
    ROLE_DEFINITIONS: TASKLIST_ROLE_DEFINITIONS,
    ROLES: TASKLIST_ROLES,
    ROLE_OPTIONS: TASKLIST_ROLE_OPTIONS,
    ROLE_VALUES: TASKLIST_ROLE_VALUES,
    NON_MANAGER_ROLE_VALUES: TASKLIST_NON_MANAGER_ROLE_VALUES,
    BOT_LIST: [
      { name: "Lease Schedule", value: "Lease Schedule" },
      { name: "Borrowing Schedule", value: "Borrowing Schedule" },
      { name: "Transaction Matching", value: "Transaction Matching" },
      { name: "Accruals", value: "Accruals" },
    ],
    LEASE_SCHEDULE_ITEMS: [
      "BS-ROU Asset",
      "BS-Lease Liability",
      "PL-Amortization Cost",
      "PL-Interest Expense",
      "BS-Security Deposit",
      "BS-SD-Interest Income",
    ],
    PRIORITY_LIST: ["High", "Medium", "Low"],
    ERROR_MESSAGES: {
      GL_CODE_REQUIRED:
        'At least one GL must be linked for the "Account Reconciliation" task type.',
      TASK_DESCRIPTION_REQUIRED: 'Task Name is required',
      CATEGORY_REQUIRED: 'Category is required',
      SUBCATEGORY_REQUIRED: 'Sub Category is required',
      CATEGORY_NOT_FROM_COA: 'Category must be selected from Chart of Accounts',
      SUBCATEGORY_NOT_FROM_COA: 'Sub Category must be selected from Chart of Accounts',
      TASK_TYPE_REQUIRED: 'Task Type is required',
      TAG_REQUIRED: 'Tag is required',
      CYCLE_TYPE_REQUIRED: 'Cycle Type is required',
      PRIORITY_REQUIRED: 'Priority is required',
      LOCATION_REQUIRED: 'Location is required',
      PREPARER_REQUIRED: 'Preparer is required',
      APPROVER_REQUIRED: 'Approver is required',
      INACTIVE_PREPARER: 'Inactive preparer(s) are selected. Please remove them and choose active users only.',
      INACTIVE_APPROVER: 'Inactive approver(s) are selected. Please remove them and choose active users only.',
      INACTIVE_SIGN_OFF_APPROVER: 'Inactive sign-off approver(s) are selected. Please remove them and choose active users only.',
      INACTIVE_ASSIGNMENT_USERS: 'Please remove inactive users before saving.',
      PREPARER_APPROVER_OVERLAP: 'Preparer and Approver cannot be the same user',
      ACCOUNT_NO_NOT_ALLOWED_NORMAL: 'Account No not allowed for Normal task',
      ACCOUNT_NO_REQUIRED: 'Account No required',
      DUE_DAYS_OUT_OF_RANGE: 'Due Days must be between -200 and 200',
      DUE_DATE_REQUIRED: 'Due Date is required',
    },
    BULK_EDIT_HEADERS_MASTER: [
      { text: "Task Type", value: "task_type" },
      { text: "Reference", value: "ref" },
      { text: "Task Name", value: "description" },
      { text: "Due Days", value: "duedays" },
      { text: "Category", value: "category" },
      { text: "Sub Category", value: "subcategory" },
      { text: "Priority", value: "priority" },
      { text: "Preparer", value: "preparer" },
      { text: "Approver", value: "approver" },
      { text: "Purpose", value: "purpose" },
      { text: "Instruction", value: "instruction" },
      { text: "Frequency", value: "tags" },
      { text: "No. of Evidences", value: "evidences" },
      { text: "Account No", value: "glcode" },
      { text: "Location", value: "location" },
    ],
    BULK_EDIT_HEADERS_CLOSE_CYCLE: [
      { text: "Task Type", value: "task_type" },
      { text: "Reference", value: "ref" },
      { text: "Task Name", value: "description" },
      { text: "Due Date (dd/mm/yyyy)", value: "duedate" },
      { text: "Category", value: "category" },
      { text: "Sub Category", value: "subcategory" },
      { text: "Purpose", value: "purpose" },
      { text: "Instruction", value: "instruction" },
      { text: "Priority", value: "priority" },
      { text: "Preparer", value: "preparer" },
      { text: "Approver", value: "approver" },
      { text: "Sign-Off Approver", value: "sign_off_approver" },
      { text: "Frequency", value: "tags" },
      { text: "No. of Evidences", value: "evidences" },
      { text: "Linked Gl", value: "linkedgl" },
      { text: "Location", value: "location" },
      { text: "Assertions", value: "assertions" },
    ],
  },
  EXPECTED_HEADERS_MASTER: [
    'Task Name', 'Category', 'Sub Category', 'Location', 'Assertions',
    'Purpose', 'Instruction', 'Preparer', 'Approver', 'Sign-Off Approver', 'Frequency',
    'Due Days', 'Account No', 'Priority', 'No. of Evidences', 'Enforce Threshold'
      ],
  EXPECTED_HEADERS_CLOSE_CYCLE: [
    "Task Name",
    "Category",
    "Sub Category",
    "Location",
    "Assertions",
    "Purpose",
    "Instruction",
    "Preparer",
    "Approver",
    "Sign-Off Approver",
    "Frequency",
    "Due Days",
    "Account No",
    "Priority",
    "No. of Evidences",
    "Evidence Required",
    "Enforce Threshold",
    "Auto-Approve",
  ],
      
  ASK_NOA_LABEL: 'Ask Noa',
  BOT_NAME: 'Noa Agent',
  CUSTOM:"custom_",
  MATERIALITYTYPES:['materialityrulevel','materialitygllevel','bulkmaterialityrulevel','bulkmaterialitygllevel','filematerialityrulevel','filematerialitygllevel','materialitysllevel','bulkmaterialitysllevel','filematerialitysllevel'],
  
  // Reconciliation Table Configuration
  RECONCILIATION_TABLE: {
    COLUMN_WIDTHS: {
      PARTICULARS: '45%',
      FILE_REFERENCE: '25%',
      SUB_AMOUNT: '15%',
      FINAL_AMOUNT: '15%'
    },
    COLUMN_WIDTHS_SUMMARY: {
      DESCRIPTION: '50%',
      SUB_AMOUNT: '25%',
      FINAL_AMOUNT: '25%'
    },
    ANIMATION_DURATION: 200,
    TABLE_TYPES: [
      { text: 'Entity View', value: 'entity' },
      { text: 'GL View', value: 'gl' }
    ],
    CLASSES: {
      TABLE_BASE: 'reconciliation-table',
      HEADER_ROW: 'table-header-row',
      GROUP_HEADER: 'group-header',
      GROUP_ITEM: 'group-item',
      COLLAPSIBLE: 'collapsible-header'
    },
    COLORS: {
      HEADER_BG: '#f5f5f5',
      HEADER_BORDER: '#e0e0e0',
      GROUP_HEADER_BG: '#f0f0f0',
      GROUP_HEADER_HOVER: '#e8e8e8',
      GROUP_ITEM_BG: '#fafafa',
      BORDER: '#e0e0e0'
    },
    SPACING: {
      CELL_PADDING: '8px 12px',
      HEADER_PADDING: '12px 16px'
    },
    // Static text constants
    TEXT: {
      RECONCILIATION_SUMMARY: 'Reconciliation Summary',
      RECONCILIATION_DETAILS: 'Reconciliation Details',
      RECON_AMOUNT: 'Recon Amount',
      RECONCILLATION_SUMMARY_API: 'Reconcillation Summary', // Note: API returns with typo
      RECONCILIATION_SUMMARY_API: 'Reconciliation Summary', // Note: API returns with typo
      UNEXPLAINED_DIFFERENCE: 'Unexplained difference',
      TRAIL_BALANCE_TOTAL: 'Trail Balance',
      TOTAL_EXCLUDED: 'Total Excluded',
      TOTAL_AMOUNT_AS_PER: 'total amount as per',
      TOTAL_AMOUNT_AS_PER_TABLE2: 'total amount as per table 2',
      UNMATCHED_TOTAL_IN: 'unmatched total in',
      UNMATCHED_TOTAL_IN_TABLE1: 'unmatched total in table 1',
      GROUP_TABLE_1_TOTAL: 'total in table 1',
      GROUP_TABLE_2_TOTAL: 'total in table 2',
      ADD_PREFIX: 'Add : ',
      LESS_PREFIX: 'Less : ',
      LOADING_TEXT: 'Loading reconciliation data...',
      NO_DATA_AVAILABLE: 'No data available',
      ANALYSIS_NOT_RUN: 'Analysis has not yet been run',
      EMPTY_STATE_HEADING: "Analysis hasn't been run yet",
      EMPTY_STATE_DESCRIPTION:
        'Run the reconciliation analysis to match transactions, identify variances, and flag unexplained differences for review.',
      NO_RECONCILIATION_DETAILS: 'No Reconciliation Details Available',
      // Table headers
      DESCRIPTION: 'Description',
      SUB_AMOUNT: 'Sub Amount',
      FINAL_AMOUNT: 'Final Amount',
      PARTICULARS: 'Particulars',
      FILE_REFERENCE: 'File Reference'
    },
  },
  STYLES: {
      SIZE: {
        MINI_NAV: 64,
        EXPAND_NAV: 250
      },
      SIDE_NAV: {
        COLLAPSED: {
          OPEN_DELAY: 200,
          ANIMATION_DURATION: 160,
          CLOSE_DELAY: 350,
          FORGIVENESS_ZONE: 16
        },
        EXPANDED: {
          OPEN_DELAY: 300,
          ANIMATION_DURATION: 200,
          CLOSE_DELAY: 500,
          FORGIVENESS_ZONE: 16
        }
      },
      /** Figma side rail: 52px collapsed · 192px expanded (shared FBC + AppMenu shell) */
      SIDE_NAV_DRAWER: {
        WIDTH_COLLAPSED: 53,
        WIDTH_EXPANDED: 192,
        CLASS: {
          NAV_DRAWER: 'nav-drawer',
          HEIGHT_SCREEN: 'height-screen',
          FBC_NAV_DRAWER: 'fbc-nav-drawer',
          FBC_NAV_DRAWER_MINI: 'fbc-nav-drawer--mini',
          FBC_NAV_DRAWER_EXPANDED: 'fbc-nav-drawer--expanded',
          APP_MENU_NAV_DRAWER: 'app-menu-nav-drawer',
          LEGACY_MINI_NAV: 'mini-nav',
          LEGACY_EXPANDED_PADDING: 'pa-4'
        }
      }
  },

  /**
   * Product/subscription key identifiers used across the app.
   */
  PRODUCT_KEYS: {
    FINANCIAL_CLOSE: 'financialclose',
    VARIANCE_ANALYSIS: 'varianceanalysis',
    ADMIN: 'admin',
    PBC: 'pbc',
    TDS_AR: 'tds_ar',
    TDS_AP: 'tds_ap',
    TDS_MONTHLY: 'tds_monthly',
    EARNOUT: 'earnout',
    IA: 'ia',
    BOT: 'bot',
    FLUX: 'flux',
    HELPDESK: 'helpdesk',
  },

  /**
   * Free plan name used for expiry checks.
   */
  PLAN_FREE: 'Free',

  /**
   * Global/shared route paths for non-FC modules.
   */
  ROUTE_PATHS: {
    ROOT: '/',
    VA_ACTUAL_ANALYSIS: '/varianceanalysis/actualanalysis',
    VA_QUEUE: '/varianceanalysis/queue',
    ADMIN_DASHBOARD: '/admin/dashboard',
    PBC_PROJECT: '/pbc/project',
    TDS_HOME: '/tds',
    EARNOUT_PROJECT: '/earnout/project',
    IA_PROJECT: '/ia/project',
    BOT_PROJECT: '/bot/project',
    FLUX_PROJECT: '/flux/project',
    HELPDESK_QUERIES: '/helpdesk/queries',
    MANUALS: '/manuals/index',
    MANUALS_ROOT: '/manuals',
  },

  /**
   * Global API endpoints used outside module stores (e.g., base.vue).
   */
  API_URLS: {
    PROFILE_GET_DETAILS: '/v2/profile/getdetails',
    AUTH_GET_PERMISSION: '/v2/auth/getpermission',
    AUTH_LOGOUT: '/v2/auth/logout',
    AUTH_LOCAL: '/v2/auth/local',
    AUTH_CHECKLOGIN: '/v2/auth/checklogin',
    AUTH_REFRESH: '/v2/auth/refresh',
    AUTH_SSO_COMPLETE: '/v2/auth/sso/complete',
    AUTH_LOGIN_SCREENS: '/v2/auth/login-screens',
    AUTH_FORGOT_PASSWORD: '/v2/auth/forgotpassword',
    AUTH_RESEND_PASSWORD: '/v2/auth/resendpassword',
    AUTH_CHECK_SET_PASSWORD: '/v2/auth/checksetpassword',
    AUTH_SET_PASSWORD: '/v2/auth/setpassword',
    COMMENTARY_GET_PAGES: '/v2/commentarybundling/getpages',
    PROFILE_UPDATE: '/v2/userprofile/add',
    ERP_SYNC_TRIAL_BALANCE: 'v2/erp/sync-trialbalance',
    SSO_DOMAINS: '/v3/client/sso/domains',
    SSO_DOMAINS_BOOTSTRAP: '/v3/client/sso/domains/bootstrap',
    SSO_CONNECTIONS: '/v3/client/sso/connections',
  },

  /**
   * localStorage key names.
   */
  STORAGE_KEYS: {
    ENTITY: 'Entity',
    LABELS_LIST: 'labelsList',
    LAST_LOGIN_TIME: 'lastLoginTime',
    USER_OBJ: 'userObj',
    POST_LOGIN_RETURN_PATH: 'post_login_return_path',
    MANUALS_RETURN_PATH: 'manuals_return_path',
  },

  /**
   * True when the route is the manuals module (`/manuals` or `/manuals/...`).
   * Used to keep product chrome (SideNav, FBC cycle) while that page is open.
   */
  isManualsPath(path = '') {
    const root = this.ROUTE_PATHS.MANUALS_ROOT;
    return path === root || String(path).startsWith(`${root}/`);
  },

  /**
   * Product route the user was on before opening manuals.
   * Empty when sessionStorage is unavailable or nothing was stored.
   */
  getManualsReturnPath() {
    try {
      return sessionStorage.getItem(this.STORAGE_KEYS.MANUALS_RETURN_PATH) || '';
    } catch (_) {
      return '';
    }
  },

  /**
   * Remember the current product route so SideNav/menu stay on that product
   * after navigating to `/manuals`. Ignores manuals paths so they are not nested.
   * Kept in sessionStorage for the whole manuals visit (SideNav re-reads it);
   * clear when leaving `/manuals`.
   */
  rememberManualsReturnPath(fromPath) {
    if (!fromPath || this.isManualsPath(fromPath)) return;
    try {
      sessionStorage.setItem(this.STORAGE_KEYS.MANUALS_RETURN_PATH, fromPath);
    } catch (_) { /* storage unavailable */ }
  },

  /**
   * Drop the stored product route once the user leaves manuals.
   */
  clearManualsReturnPath() {
    try {
      sessionStorage.removeItem(this.STORAGE_KEYS.MANUALS_RETURN_PATH);
    } catch (_) { /* storage unavailable */ }
  },

  /**
   * Route used for product menu, FBC chrome, and manuals pack lookup.
   * On `/manuals` this is the stored return path; otherwise the current path.
   */
  menuContextPath(path = '') {
    const current = String(path || '');
    if (!this.isManualsPath(current)) return current;
    return this.getManualsReturnPath() || current;
  },

  /**
   * Product key for manuals availability (`financialclose`, `varianceanalysis`, …).
   * Taken from the first path segment when it is a known PRODUCT_KEYS value.
   */
  resolveManualsProduct(path = '') {
    const first = String(path || '').split('/').filter(Boolean)[0] || '';
    return Object.values(this.PRODUCT_KEYS).includes(first) ? first : '';
  },

  /**
   * Vuex module store prefix.
   */
  STORE_MODULE_PREFIX: 'm_',

  /**
   * Shared API payload keys.
   * Validation errors are nested under `data[0][VALIDATION_INDEX_KEY]`.
   */
  API: {
    VALIDATION_INDEX_KEY: 'index0',
  },

  /** Keys a read-only time input still accepts; Ctrl/Cmd shortcuts are allowed separately. */
  READONLY_TIME_INPUT_ALLOWED_KEYS: Object.freeze(['Tab', 'Escape', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']),

  /**
   * App registry — declarative list of all products for building the applist.
   * Each entry maps a product code to its display configuration.
   * `permissionKey`: property name on the permission object that must be truthy (if any).
   * `codes`: array of product codes that satisfy this app entry.
   */
  APP_REGISTRY: [
    { codes: ['pbc'], name: 'pbc', displayname: 'Information Workflow', color: 'crisk', avatartext: 'I', link: '/pbc/project', permissionKey: 'pbc_project' },
    { codes: ['tds_ar', 'tds_ap', 'tds_monthly'], name: 'tds', displayname: 'Witholding Tax', color: 'ccompliance', avatartext: 'T', link: '/tds', permissionKey: 'tds_monthly', permissionOnlyCodes: ['tds_monthly'] },
    { codes: ['earnout'], name: 'earnout', displayname: 'Earnouts', color: 'ctransaction', avatartext: 'E', link: '/earnout/project' },
    { codes: ['financialclose'], name: 'financialclose', displayname: 'Financial Close', color: 'caccounting', avatartext: 'F', link: '/financialclose/home' },
    { codes: ['ia'], name: 'ia', displayname: 'Internal Audit', color: 'crisk', avatartext: 'IA', link: '/ia/project' },
    { codes: ['bot'], name: 'bot', displayname: null, color: 'crisk', avatartext: 'A', link: '/bot/project', permissionKey: 'bot_project', useBotName: true },
    { codes: ['flux'], name: 'flux', displayname: 'Flux Management', color: 'caccounting', avatartext: 'F', link: '/flux/project' },
    { codes: ['helpdesk'], name: 'helpdesk', displayname: 'Help Desk', color: 'primary', avatartext: 'H', link: '/helpdesk/queries' },
    { codes: ['varianceanalysis'], name: 'varianceanalysis', displayname: 'Commentary', color: 'caccounting', avatartext: 'C', link: '/varianceanalysis/actualanalysis' },
    { codes: ['admin'], name: 'admin', displayname: 'Admin', color: 'primary', avatartext: 'A', link: '/admin/client' },
  ],

};
